'use client';

import { useEffect, useRef } from 'react';
import {
  useSessionUIContext,
  type MemberGlimpseOwnerToken,
} from './session-ui-context';

interface UseSessionMemberGlimpseBridgeInput {
  /** The host's "which member is glimpsed" state; null = closed. */
  memberId: string | null;
  /** Clears the host's state when the glimpse closes (from either side). */
  onClose: () => void;
  /** Stacking offset when the glimpse opens above the host's own modal. */
  modalIndex?: number;
  /** The clicked trigger's rect — the glimpse morph origin (null/omitted = centre grow). */
  originRect?: DOMRect | null;
  /** The clicked trigger element — focus returns here when the glimpse closes. */
  restoreFocusTo?: HTMLElement | null;
}

/**
 * Bridges a host's own glimpse state into the single session-level member-glimpse
 * host. Hosts must never mount their own Cmemberglimpse: a closed local mount still
 * runs the overlay's interaction hook, which owns a window wheel listener.
 */
export function useSessionMemberGlimpseBridge({
  memberId,
  onClose,
  modalIndex,
  originRect = null,
  restoreFocusTo = null,
}: UseSessionMemberGlimpseBridgeInput): void {
  const { openMemberGlimpse, closeMemberGlimpse } = useSessionUIContext();
  const onCloseRef = useRef(onClose);
  // Read at open time via a ref: the rect is captured per click (set alongside
  // memberId), so it must not itself re-trigger the open effect.
  const originRectRef = useRef(originRect);
  // Once a member is open, modal-index updates must retain that opening's rect.
  // A later same-member click may update the host's rect state without reopening;
  // it must not silently replace the collapse target on a subsequent stack update.
  const activeOriginRectRef = useRef<DOMRect | null>(null);
  const restoreFocusToRef = useRef(restoreFocusTo);
  const activeRestoreFocusToRef = useRef<HTMLElement | null>(null);
  const openedMemberIdRef = useRef<string | null>(null);
  // The token scopes this bridge's closes: a glimpse superseded by another opener
  // must not be closed (or have its state clobbered) by the stale owner.
  const ownedTokenRef = useRef<MemberGlimpseOwnerToken | null>(null);
  // Each effect setup bumps the generation; a cleanup followed by another setup
  // (dep change, StrictMode replay) is NOT an unmount and must not close the glimpse.
  const setupGenerationRef = useRef(0);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    originRectRef.current = originRect;
  }, [originRect]);

  useEffect(() => {
    restoreFocusToRef.current = restoreFocusTo;
  }, [restoreFocusTo]);

  useEffect(() => {
    setupGenerationRef.current += 1;
    const setupGeneration = setupGenerationRef.current;

    if (memberId != null) {
      if (
        ownedTokenRef.current == null ||
        openedMemberIdRef.current !== memberId
      ) {
        activeOriginRectRef.current = originRectRef.current;
        activeRestoreFocusToRef.current = restoreFocusToRef.current;
      }
      openedMemberIdRef.current = memberId;
      // reuseOwnerToken makes same-bridge re-opens (member switch, stacking change,
      // StrictMode replay) in-place updates instead of self-replacements.
      ownedTokenRef.current = openMemberGlimpse({
        memberId,
        originRect: activeOriginRectRef.current,
        modalIndex,
        restoreFocusTo: activeRestoreFocusToRef.current,
        reuseOwnerToken: ownedTokenRef.current ?? undefined,
        onClose: () => {
          ownedTokenRef.current = null;
          openedMemberIdRef.current = null;
          activeOriginRectRef.current = null;
          activeRestoreFocusToRef.current = null;
          onCloseRef.current();
        },
      });
    } else if (ownedTokenRef.current != null) {
      const ownedToken = ownedTokenRef.current;
      ownedTokenRef.current = null;
      openedMemberIdRef.current = null;
      activeOriginRectRef.current = null;
      activeRestoreFocusToRef.current = null;
      closeMemberGlimpse(ownedToken);
    }

    return () => {
      // Deferred so a subsequent setup (which bumps the generation) can veto it:
      // only a REAL host unmount takes its open glimpse down with it.
      window.setTimeout(() => {
        if (setupGenerationRef.current !== setupGeneration) return;
        if (ownedTokenRef.current == null) return;
        const ownedToken = ownedTokenRef.current;
        ownedTokenRef.current = null;
        openedMemberIdRef.current = null;
        activeOriginRectRef.current = null;
        activeRestoreFocusToRef.current = null;
        closeMemberGlimpse(ownedToken);
      }, 0);
    };
  }, [memberId, modalIndex, openMemberGlimpse, closeMemberGlimpse]);
}
